import * as React from 'react';
import { useState } from 'react';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import type { SmallButtonProps } from '~shared/components/buttons';
import { SmallButton } from '~shared/components/buttons';
import { IconAI } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import { useStartPoll } from '~shared/polling';
import { useGenerateInsightsMutation } from '~teacher/hook';
import { handleInsightsError } from '~teacher/hook/activity/insights/handleInsightsError';

interface InsightsGenerateInsightsButtonProps {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
}

export function InsightsGenerateInsightsButton({
  isLoading,
  onClickCapture,
}: InsightsGenerateInsightsButtonProps) {
  return (
    <SmallButton
      noWrap
      isLoading={isLoading}
      onClickCapture={onClickCapture}
      xPadding={0}
    >
      <div className="flex items-center pl-2 pr-4">
        <IconAI />
        {translate('Analyze Report')}
      </div>
    </SmallButton>
  );
}

interface UseInsightsGenerateInsightsButtonParams {
  activityInstanceId: number;
  setShowPaywall: (show: boolean) => void;
  averageScoreRate?: number | null;
}

interface UseInsightsGenerateInsightsButtonResult {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
  showNoInsightsDialog: boolean;
  setShowNoInsightsDialog: (show: boolean) => void;
}

export function useInsightsGenerateInsightsButton({
  activityInstanceId,
  setShowPaywall,
  averageScoreRate,
}: UseInsightsGenerateInsightsButtonParams): UseInsightsGenerateInsightsButtonResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showNoInsightsDialog, setShowNoInsightsDialog] = useState(false);
  const postGenerateInsightsMutation = useGenerateInsightsMutation();
  const { startPoll } = useStartPoll();

  const onClickCapture: SmallButtonProps['onClickCapture'] = async (event) => {
    event.stopPropagation();

    // Check if avg score is 100%
    if (averageScoreRate === 100) {
      setShowNoInsightsDialog(true);
      return;
    } else {
      setShowNoInsightsDialog(false);
    }

    setIsGenerating(true);
    sendTeacherAmplitudeEvent(EventTypes.ClickGenerateAIInsights);

    try {
      await postGenerateInsightsMutation.mutateAsync({
        activityInstanceId,
      });

      // Start polling for insights using the new generic polling system
      startPoll({
        id: `insights-${activityInstanceId}`,
        type: 'ai-insights',
        metadata: {
          activityInstanceId,
        },
        preserveHistory: false,
      });
    } catch (error) {
      // Handle the error based on its type
      const errorResult = handleInsightsError(error);

      switch (errorResult.type) {
        case 'paywall':
          setShowPaywall(true);
          break;
        case 'toast':
          toast.critical(errorResult.message);
          break;
        case 'silent':
          /* Component will handle showing the message
            Currently runs when InsufficientDataError is thrown
          */
          break;
      }

      // Don't start polling on error - the mutation onError handler
      // will update the query cache to show the appropriate state
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isLoading: isGenerating,
    onClickCapture: onClickCapture,
    showNoInsightsDialog,
    setShowNoInsightsDialog,
  };
}
